import { FormControl, MenuItem } from '@mui/material';
import { SelectorValueTypes } from '../../../../types/enums/SelectorValueTypes';
import SweepSelect from '../../../common/SweepSelect';
import { selectFirstValueTypeOption } from '../utils/selectFirstValueTypeOption';
import { isFormulaSupported } from '../utils/isFormulaSupported';
import {
  getVerifiedUrlOptionsByType,
  SfUrlVerificationFieldTypes,
} from '../utils/getVerifiedUrlByType';
import { SfdcDisplayType } from '../../../../types/fields';
import { AutomationActionRowProps } from '../types';
import { useSweepFieldsInfo } from '../../../../sweep-fields/useSweepFieldsInfo';
import { capitalizeString } from '../../../../lib/capitalizeFirstLetter';

export const TypeSelect = ({
  readonly,
  onChange,
  _field,
  excludeValueType,
  showVerifiedUrlOption,
  setFormulaEditorStatePerKey,
  setValueFromAnotherObjectEditorStatePerKey,
  isShowWebhookOption,
}: Pick<
  AutomationActionRowProps,
  'readonly' | 'onChange' | 'excludeValueType' | 'showVerifiedUrlOption' | 'isShowWebhookOption'
> & {
  _field: AutomationActionField;
  setFormulaEditorStatePerKey:
    | (({ key, value }: { key: string; value: boolean }) => void)
    | undefined;
  setValueFromAnotherObjectEditorStatePerKey:
    | (({ key, value }: { key: string; value: boolean }) => void)
    | undefined;
}) => {
  const { getFieldInfoFromId } = useSweepFieldsInfo();
  const fieldInfo = getFieldInfoFromId(_field._fieldIds);
  const sfDisplayType = fieldInfo?.sfDisplayType;

  const showFormulaOption = isFormulaSupported({
    sfDisplayType: fieldInfo?.sfDisplayType,
    isRestrictedPicklist: fieldInfo?.isRestrictedPicklist,
  });

  const showPriorValueTypeAutomation =
    excludeValueType && !excludeValueType(SelectorValueTypes.PRIOR);

  const isFieldTypeWithAnyOptions = !!getVerifiedUrlOptionsByType(
    sfDisplayType,
    showVerifiedUrlOption,
  )?.length;

  const isShowUrlVerificationOption =
    showVerifiedUrlOption &&
    isFieldTypeWithAnyOptions &&
    sfDisplayType &&
    SfUrlVerificationFieldTypes.includes(sfDisplayType);

  const hideAnotherObjectValueTypeAutomation =
    excludeValueType && excludeValueType(SelectorValueTypes.FROM_ANOTHER_OBJECT);

  return (
    <FormControl sx={{ width: '100%' }}>
      <SweepSelect
        SelectProps={{
          disabled: !_field._fieldIds.length || readonly,
          placeholder: 'Choose Type',
          value: (_field?.valueType || (sfDisplayType as string)) ?? '',
          onChange: (event) => {
            setFormulaEditorStatePerKey &&
              setFormulaEditorStatePerKey({ key: _field._id, value: false });
            setValueFromAnotherObjectEditorStatePerKey &&
              setValueFromAnotherObjectEditorStatePerKey({ key: _field._id, value: false });
            const val = event.target.value;
            if (
              val === SelectorValueTypes.REFERENCE ||
              val === SelectorValueTypes.RELATIVE ||
              val === SelectorValueTypes.FORMULA ||
              val === SelectorValueTypes.PRIOR ||
              val === SelectorValueTypes.VERIFY_URL ||
              val === SelectorValueTypes.FROM_ANOTHER_OBJECT ||
              val === SelectorValueTypes.WEBHOOK_RESULT
            ) {
              onChange({
                ..._field,
                valueType: val as SelectorValueTypes,
                value: '',
              });
              if (val === SelectorValueTypes.FORMULA) {
                setFormulaEditorStatePerKey &&
                  setFormulaEditorStatePerKey({ key: _field._id, value: true });
              }
              if (val === SelectorValueTypes.FROM_ANOTHER_OBJECT) {
                setValueFromAnotherObjectEditorStatePerKey &&
                  setValueFromAnotherObjectEditorStatePerKey({ key: _field._id, value: true });
              }
            } else {
              selectFirstValueTypeOption({ onChange, _field });
            }
          },
          renderValue: (val) => {
            if (
              val === SelectorValueTypes.RELATIVE ||
              val === SelectorValueTypes.REFERENCE ||
              val === SelectorValueTypes.FORMULA
            ) {
              return val as string;
            }
            if (val === SelectorValueTypes.LITERAL) {
              return capitalizeString(sfDisplayType as string);
            }
            if (val === SelectorValueTypes.PRIOR) {
              return 'Prior value of';
            }
            if (val === SelectorValueTypes.FROM_ANOTHER_OBJECT) {
              return 'Value from another object';
            }
            if (val === sfDisplayType) {
              return capitalizeString(val as string) || '';
            }
            if (val === SelectorValueTypes.VERIFY_URL) {
              return 'URL verification results';
            }
            if (val === SelectorValueTypes.WEBHOOK_RESULT) {
              return 'Value from webhook response';
            }
          },
        }}
      >
        <MenuItem value={SelectorValueTypes.LITERAL} hidden></MenuItem>
        {isShowWebhookOption && (
          <MenuItem value={SelectorValueTypes.WEBHOOK_RESULT}>Value from webhook response</MenuItem>
        )}
        <MenuItem value={sfDisplayType}>{capitalizeString(sfDisplayType as string)}</MenuItem>
        <MenuItem value={SelectorValueTypes.REFERENCE}>
          Value from the record that triggered this automation
        </MenuItem>
        {(sfDisplayType === SfdcDisplayType.DATETIME ||
          (sfDisplayType === SfdcDisplayType.DATE && !isShowWebhookOption)) && (
          <MenuItem value={SelectorValueTypes.RELATIVE}>Relative Date</MenuItem>
        )}
        {showFormulaOption && <MenuItem value={SelectorValueTypes.FORMULA}>Formula</MenuItem>}
        {!isShowWebhookOption && !hideAnotherObjectValueTypeAutomation && (
          <MenuItem value={SelectorValueTypes.FROM_ANOTHER_OBJECT}>
            Value from another object
          </MenuItem>
        )}
        {showPriorValueTypeAutomation && (
          <MenuItem value={SelectorValueTypes.PRIOR}>Field's prior value</MenuItem>
        )}
        {isShowUrlVerificationOption && (
          <MenuItem value={SelectorValueTypes.VERIFY_URL}>URL verification results</MenuItem>
        )}
      </SweepSelect>
    </FormControl>
  );
};
